import type { LocationResponse } from "@safeplate/contracts";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@safeplate/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Archive, MapPin, Pencil, Plus } from "lucide-react";
import { useId, useState, type FormEvent } from "react";

import { displayError } from "../api/errors.js";
import { EmptyState } from "../components/states.js";
import { useShell } from "../shell/shell-context.js";
import { createLocation, updateLocation } from "./setup-api.js";

interface LocationFormValue {
  address: Record<string, string>;
  council_area: string | null;
  email: string | null;
  name: string;
  phone: string | null;
  status: "active" | "setup";
  timezone: string;
  trading_name: string | null;
}

function readForm(form: FormData): LocationFormValue {
  const text = (key: string) => String(form.get(key) ?? "").trim();
  const nullable = (key: string) => text(key) || null;
  return {
    address: {
      ...(text("line1") ? { line1: text("line1") } : {}),
      ...(text("suburb") ? { suburb: text("suburb") } : {}),
      ...(text("city") ? { city: text("city") } : {}),
      ...(text("postcode") ? { postcode: text("postcode") } : {}),
    },
    council_area: nullable("council_area"),
    email: nullable("email"),
    name: text("name"),
    phone: nullable("phone"),
    status: text("status") === "setup" ? "setup" : "active",
    timezone: text("timezone") || "Pacific/Auckland",
    trading_name: nullable("trading_name"),
  };
}

function LocationEditor({
  current,
  onClose,
}: {
  current?: LocationResponse;
  onClose: () => void;
}) {
  const shell = useShell();
  const queryClient = useQueryClient();
  const prefix = useId();
  const save = useMutation({
    mutationFn: (input: LocationFormValue) =>
      current
        ? updateLocation(current.id, input)
        : createLocation({ ...input, licensable: true }),
    onSuccess: async () => {
      await shell.refreshLocations();
      await queryClient.invalidateQueries({
        queryKey: ["locations", shell.activeOrganisationId],
      });
      onClose();
    },
  });
  const address = current?.address ?? {};
  const field = (
    name: string,
    label: string,
    value?: string | null,
    type = "text",
  ) => (
    <div>
      <Label htmlFor={`${prefix}-${name}`}>{label}</Label>
      <Input
        className="mt-1.5"
        defaultValue={value ?? ""}
        id={`${prefix}-${name}`}
        name={name}
        type={type}
      />
    </div>
  );

  return (
    <form
      className="mt-5 rounded-2xl border border-emerald-900/10 bg-emerald-50/60 p-4 sm:p-5"
      onSubmit={(event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        save.mutate(readForm(new FormData(event.currentTarget)));
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor={`${prefix}-name`}>Location name</Label>
          <Input
            className="mt-1.5"
            defaultValue={current?.name ?? ""}
            id={`${prefix}-name`}
            name="name"
            placeholder="Ponsonby kitchen"
            required
          />
        </div>
        {field("trading_name", "Trading name", current?.trading_name)}
        {field(
          "line1",
          "Street address",
          typeof address.line1 === "string" ? address.line1 : "",
        )}
        {field(
          "suburb",
          "Suburb",
          typeof address.suburb === "string" ? address.suburb : "",
        )}
        {field(
          "city",
          "City",
          typeof address.city === "string" ? address.city : "",
        )}
        {field(
          "postcode",
          "Postcode",
          typeof address.postcode === "string" ? address.postcode : "",
        )}
        {field("council_area", "Council area", current?.council_area)}
        {field("phone", "Phone", current?.phone)}
        {field("email", "Email", current?.email, "email")}
        {field("timezone", "Timezone", current?.timezone ?? "Pacific/Auckland")}
        <div>
          <Label htmlFor={`${prefix}-status`}>Setup status</Label>
          <select
            className="mt-1.5 h-11 w-full rounded-xl border border-stone-300 bg-surface px-3.5 text-sm"
            defaultValue={current?.status === "setup" ? "setup" : "active"}
            id={`${prefix}-status`}
            name="status"
          >
            <option value="setup">Still setting up</option>
            <option value="active">Active</option>
          </select>
        </div>
      </div>
      {save.error ? (
        <p className="mt-4 text-sm font-semibold text-red-800" role="alert">
          {displayError(save.error)}
        </p>
      ) : null}
      <div className="mt-5 flex justify-end gap-2">
        <Button onClick={onClose} variant="ghost">
          Cancel
        </Button>
        <Button disabled={save.isPending} type="submit">
          {save.isPending
            ? "Saving…"
            : current
              ? "Save location"
              : "Create location"}
        </Button>
      </div>
    </form>
  );
}

export function LocationPanel({ canManage }: { canManage: boolean }) {
  const shell = useShell();
  const [editing, setEditing] = useState<LocationResponse | "new" | null>(null);
  const archive = useMutation({
    mutationFn: (id: string) => updateLocation(id, { status: "archived" }),
    onSuccess: () => shell.refreshLocations(),
  });

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle>Locations</CardTitle>
          <p className="mt-1 text-sm leading-5 text-stone-500">
            Keep the registered site, council and contact details current.
          </p>
        </div>
        {canManage ? (
          <Button onClick={() => setEditing("new")} size="sm">
            <Plus className="size-4" /> Add location
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="pt-3">
        {shell.locations.length > 0 ? (
          <div className="divide-y divide-stone-100 rounded-2xl border border-stone-200 bg-stone-50/70">
            {shell.locations.map((location) => (
              <div
                className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center"
                key={location.id}
              >
                <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-emerald-100 text-emerald-900">
                  <MapPin className="size-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-black text-stone-950">{location.name}</p>
                    {location.id === shell.activeLocation?.id ? (
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[0.65rem] font-bold text-emerald-900">
                        Selected
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 text-sm text-stone-500">
                    {location.council_area ?? "Council area not added"} ·{" "}
                    {location.timezone}
                  </p>
                </div>
                {canManage ? (
                  <div className="flex gap-1">
                    <Button
                      onClick={() => setEditing(location)}
                      size="sm"
                      variant="ghost"
                    >
                      <Pencil className="size-3.5" /> Edit
                    </Button>
                    <Button
                      disabled={archive.isPending}
                      onClick={() => {
                        if (
                          globalThis.confirm(
                            "Archive this location? Its history remains available.",
                          )
                        )
                          archive.mutate(location.id);
                      }}
                      size="sm"
                      variant="ghost"
                    >
                      <Archive className="size-3.5" /> Archive
                    </Button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            calm
            description="Add the physical site whose food-safety records are being kept."
            title="No location selected"
          />
        )}
        {editing ? (
          <LocationEditor
            {...(editing === "new" ? {} : { current: editing })}
            onClose={() => setEditing(null)}
          />
        ) : null}
        {archive.error ? (
          <p className="mt-3 text-sm font-semibold text-red-800" role="alert">
            {displayError(archive.error)}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
